import {sharedPoll,stopPoll} from '@/command/poll'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertTriangle, ExternalLink, Unplug, RefreshCw, Pencil, Trash2, Plus } from 'lucide-react'
import toast from 'react-hot-toast'
import { Input, Modal, Button } from '@/components/ui'
import {
  createHubDevice,
  listWhatsAppSubscriptions,
  listWhatsappHubDevices,
  saveMetaCredentials,
  type DeviceBillingStatus,
} from '@/api/whatsapp'
import { useAuth } from '@/hooks/useAuth'
import { formatNIS } from '@/utils/currency'
import { listPartners, getAllPartnerSlots, type WaPartner, type PartnerOrgSlot } from '@/api/partners'
import { waProxy } from '@/lib/whatsappProxy'
import { supabase } from '@/lib/supabase'
import { getJumpstartChatSubscriptionForOrg } from '@/api/products'
import SubscriptionTable from '@/pages/admin/whatsapp/components/SubscriptionTable'
import WebhookFailuresRow from '@/pages/admin/whatsapp/components/WebhookFailuresRow'
import SendMessageModal from '@/pages/admin/whatsapp/components/SendMessageModal'

const CONNECT_BASE = 'https://wa.otomator.pro/connect/'
const HEALTH_POLL_MS = 30_000
const SESSIONS_POLL_MS = 10_000

const PLATFORMS = [
  { id: 'jumpstart', label: 'Jumpstart' },
  // Future platforms can be added here.
] as const

type WaSessionStatus = 'connecting' | 'qr' | 'connected' | 'disconnected'

interface WaSessionRow {
  orgId: string
  status: WaSessionStatus
  phoneNumber?: string
  webhookUrl?: string
  /** When the VPS returns it; UI defaults to jumpstart for display. */
  platform?: string
  provider?: 'baileys' | 'meta-cloud'
}

interface HealthPayload {
  status: string
  sessions: number
  connected: number
  uptime: number
  memoryMB: number
}

interface SessionsListPayload {
  sessions: WaSessionRow[]
  count: number
}

interface MergedSessionRow {
  orgId: string
  organization: string
  phoneDisplay: string
  statusKey: WaSessionStatus | 'offline'
  hasLiveSession: boolean
  noSubscription: boolean
  webhookUrl?: string
  platform?: string
  provider?: 'baileys' | 'meta-cloud'
}

function inferProviderFromLabel(label: string | null | undefined): 'meta-cloud' | undefined {
  const normalized = String(label ?? '').toLowerCase()
  if (/meta|official|רשמי/.test(normalized)) return 'meta-cloud'
  // Wizard fallback format often stores phone_number_id in label: "Name [123456789]"
  if (/\[\d{6,}\]/.test(String(label ?? ''))) return 'meta-cloud'
  return undefined
}

interface PartnerGroup {
  partner: WaPartner | null
  rows: MergedSessionRow[]
}

interface ReconnectModalProps {
  orgId: string
  currentWebhookUrl?: string
  isOpen: boolean
  onConfirm: (webhookUrl?: string) => Promise<void>
  onClose: () => void
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function formatPhone(phone?: string | null): string {
  if (!phone) return '—'
  const d = phone.replace(/\D/g, '')
  if (d.startsWith('972') && d.length >= 11) {
    const rest = d.slice(3)
    return `+972-${rest.slice(0, 2)}-${rest.slice(2, 5)}-${rest.slice(5)}`
  }
  return phone
}

function ReconnectModal({ orgId, currentWebhookUrl, isOpen, onConfirm, onClose }: ReconnectModalProps) {
  const [webhookUrl, setWebhookUrl] = useState(currentWebhookUrl ?? '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setWebhookUrl(currentWebhookUrl ?? '')
      setIsSaving(false)
    }
  }, [currentWebhookUrl, isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Reconnect Session (${orgId})`}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            loading={isSaving}
            onClick={async () => {
              setIsSaving(true)
              try {
                await onConfirm(webhookUrl.trim() || undefined)
                onClose()
              } finally {
                setIsSaving(false)
              }
            }}
          >
            Confirm Reconnect
          </Button>
        </>
      )}
    >
      <Input
        label="Webhook URL"
        placeholder="https://..."
        value={webhookUrl}
        onChange={(e) => setWebhookUrl(e.target.value)}
        hint="Optional. If empty, default backend behavior will be used."
      />
    </Modal>
  )
}

interface EditSessionTarget {
  orgId: string
  webhookUrl?: string
  platform?: string
}

interface EditSessionModalProps {
  session: EditSessionTarget | null
  isOpen: boolean
  onClose: () => void
  onSave: (
    oldOrgId: string,
    newOrgId: string,
    webhookUrl?: string,
    platform?: string
  ) => Promise<void>
}

function EditSessionModal({ session, isOpen, onClose, onSave }: EditSessionModalProps) {
  const initialOrgId = session?.orgId ?? ''
  const [editPlatform, setEditPlatform] = useState<string>(session?.platform ?? 'jumpstart')
  const [editOrgId, setEditOrgId] = useState<string>(session?.orgId ?? '')
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([])
  const [webhookUrl, setWebhookUrl] = useState(session?.webhookUrl ?? '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen && session) {
      setEditPlatform(session.platform ?? 'jumpstart')
      setEditOrgId(session.orgId)
      setWebhookUrl(session.webhookUrl ?? '')
      setIsSaving(false)
    }
  }, [isOpen, session])

  useEffect(() => {
    setOrgs([])
    if (editPlatform === 'jumpstart') {
      void supabase
        .from('organizations')
        .select('id, name')
        .order('name')
        .then(({ data }) => {
          if (data) setOrgs(data as { id: string; name: string }[])
        })
    }
  }, [editPlatform])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit session"
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            loading={isSaving}
            onClick={async () => {
              if (!editOrgId.trim()) {
                toast.error('Please select an organization.')
                return
              }
              setIsSaving(true)
              try {
                const wh = webhookUrl.trim() || undefined
                await onSave(initialOrgId, editOrgId.trim(), wh, editPlatform)
                onClose()
              } catch (e) {
                const message = e instanceof Error ? e.message : 'Failed to save session.'
                toast.error(message)
              } finally {
                setIsSaving(false)
              }
            }}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Webhook URL"
          placeholder="https://..."
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          hint="Optional. If empty, default backend behavior will be used."
        />
        <div>
          <label className="block text-sm text-gray-400 mb-1">Platform</label>
          <select
            value={editPlatform}
            onChange={(e) => {
              setEditPlatform(e.target.value)
              setEditOrgId('')
            }}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-white"
          >
            {PLATFORMS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Organization</label>
          <select
            value={editOrgId}
            onChange={(e) => setEditOrgId(e.target.value)}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-white"
            disabled={orgs.length === 0}
          >
            <option value="">— select org —</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          {!!editOrgId && editOrgId !== initialOrgId && (
            <p className="text-amber-400 text-xs mt-1">
              ⚠️ Changing org moves the active pairing to the new org (same phone; target org needs an active WhatsApp subscription).
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

function StatusCell({ status }: { status: MergedSessionRow['statusKey'] }) {
  if (status === 'connected') {
    return <span className="text-sm"><span className="mr-1.5">🟢</span>Connected</span>
  }
  if (status === 'qr') {
    return <span className="text-sm"><span className="mr-1.5">🟡</span>Waiting QR</span>
  }
  if (status === 'connecting') {
    return (
      <span className="text-sm inline-flex items-center gap-2">
        <span>🟡</span>
        <Loader2 size={14} className="animate-spin text-amber-400" />
        Connecting
      </span>
    )
  }
  if (status === 'disconnected') {
    return <span className="text-sm"><span className="mr-1.5">🔴</span>Disconnected</span>
  }
  return <span className="text-sm"><span className="mr-1.5 opacity-70">⚪</span>Offline</span>
}

interface MetaCredentialsModalProps {
  orgId: string | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

interface AddDeviceModalProps {
  isOpen: boolean
  onClose: () => void
  orgs: { id: string; name: string }[]
  onCreated: () => void
}

function AddDeviceModal({ isOpen, onClose, orgs, onCreated }: AddDeviceModalProps) {
  const { user } = useAuth()
  const priceInputRef = useRef<HTMLInputElement>(null)
  const [orgId, setOrgId] = useState('')
  const [label, setLabel] = useState('')
  const [provider, setProvider] = useState<'baileys' | 'meta-cloud'>('baileys')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [billingStatus, setBillingStatus] = useState<DeviceBillingStatus>('included')
  const [priceNis, setPriceNis] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: jsChatSub } = useQuery({
    queryKey: ['jumpstart-chat-sub', orgId],
    queryFn: () => getJumpstartChatSubscriptionForOrg(orgId),
    enabled: !!orgId && isOpen,
    staleTime: 15_000,
  })
  const isTrialOrg = jsChatSub?.status === 'trial'

  useEffect(() => {
    if (isOpen) {
      setOrgId('')
      setLabel('')
      setProvider('baileys')
      setPhoneNumber('')
      setBillingStatus('included')
      setPriceNis('')
      setNotes('')
      setSaving(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!orgId || !isOpen) return
    if (isTrialOrg) {
      setBillingStatus('extra_billed')
      setPriceNis('')
      const t = window.setTimeout(() => priceInputRef.current?.focus(), 0)
      return () => window.clearTimeout(t)
    }
    setBillingStatus('included')
    setPriceNis('')
  }, [orgId, isOpen, isTrialOrg])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Hub device"
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            loading={saving}
            onClick={async () => {
              if (!user?.email) {
                toast.error('Admin session email missing — sign in again.')
                return
              }
              if (!orgId.trim() || !label.trim()) {
                toast.error('Organization and label are required.')
                return
              }
              const priceAgorot = Math.round(Number(priceNis) * 100)
              const effectiveBilling: DeviceBillingStatus = isTrialOrg ? 'extra_billed' : billingStatus
              if (
                (effectiveBilling === 'extra_billed' || isTrialOrg) &&
                (!Number.isFinite(priceAgorot) || priceAgorot <= 0)
              ) {
                toast.error(
                  isTrialOrg
                    ? 'Trial orgs require a monthly price (₪) per device.'
                    : 'Set a positive monthly price (₪) for extra-billed devices.'
                )
                return
              }
              setSaving(true)
              try {
                await createHubDevice({
                  orgId: orgId.trim(),
                  label: label.trim(),
                  provider,
                  phoneNumber: phoneNumber.trim() || null,
                  billingStatus: effectiveBilling,
                  priceAgorot: effectiveBilling === 'extra_billed' ? priceAgorot : 0,
                  notes: notes.trim() || null,
                  userEmail: user.email,
                })
                toast.success('Device row created')
                onCreated()
                onClose()
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Failed to create device')
              } finally {
                setSaving(false)
              }
            }}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Organization</label>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-white"
          >
            <option value="">— select org —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <Input label="Label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Display name" />
        <div>
          <label className="block text-sm text-gray-400 mb-1">Provider</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as 'baileys' | 'meta-cloud')}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-white"
          >
            <option value="baileys">baileys</option>
            <option value="meta-cloud">meta-cloud</option>
          </select>
        </div>
        <Input
          label="Phone number (optional)"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+972…"
        />
        <div>
          <label className="block text-sm text-gray-400 mb-1">Billing status</label>
          <select
            value={billingStatus}
            onChange={(e) => setBillingStatus(e.target.value as DeviceBillingStatus)}
            disabled={isTrialOrg}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-white disabled:opacity-60"
          >
            <option value="included">included</option>
            <option value="extra_billed">extra_billed</option>
            <option value="free">free</option>
            <option value="comped">comped</option>
          </select>
          {isTrialOrg ? (
            <p className="text-[11px] text-amber-200/90 mt-1">JumpStart Chat is on trial — devices bill as extra_billed.</p>
          ) : null}
        </div>
        <Input
          ref={priceInputRef}
          label="Price / month (₪)"
          type="number"
          min="0"
          step="0.01"
          value={priceNis}
          onChange={(e) => setPriceNis(e.target.value)}
          disabled={!isTrialOrg && billingStatus !== 'extra_billed'}
          required={isTrialOrg || billingStatus === 'extra_billed'}
          hint={
            isTrialOrg
              ? 'Trial orgs pay per device — no free quota.'
              : billingStatus === 'extra_billed'
                ? 'Required for extra-billed devices'
                : 'Only when billing is extra_billed'
          }
        />
        <div>
          <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-[#1a1d27] border border-[#2a2d3e] rounded-lg px-3 py-2 text-sm text-white"
          />
        </div>
      </div>
    </Modal>
  )
}

function MetaCredentialsModal({ orgId, isOpen, onClose, onSaved }: MetaCredentialsModalProps) {
  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [wabaId, setWabaId] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setPhoneNumberId('')
      setWabaId('')
      setAccessToken('')
      setSaving(false)
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Meta Cloud API"
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            loading={saving}
            onClick={async () => {
              if (!orgId || !phoneNumberId.trim() || !wabaId.trim() || !accessToken.trim()) {
                toast.error('All fields are required.')
                return
              }
              setSaving(true)
              try {
                await saveMetaCredentials({
                  orgId,
                  phoneNumberId: phoneNumberId.trim(),
                  wabaId: wabaId.trim(),
                  accessToken: accessToken.trim(),
                })
                toast.success('Meta credentials saved to Otomator + JumpStart')
                onSaved()
                onClose()
              } catch (e) {
                toast.error((e as Error).message)
              } finally {
                setSaving(false)
              }
            }}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Phone Number ID"
          placeholder="123456789012345"
          value={phoneNumberId}
          onChange={(e) => setPhoneNumberId(e.target.value)}
          hint="From Meta WhatsApp Business Manager → Phone Numbers"
        />
        <Input
          label="WhatsApp Business Account ID (WABA ID)"
          placeholder="987654321098765"
          value={wabaId}
          onChange={(e) => setWabaId(e.target.value)}
          hint="From Meta Business Manager → WhatsApp Accounts"
        />
        <Input
          label="Permanent Access Token"
          placeholder="EAAxxxxx..."
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          hint="System User permanent token with whatsapp_business_messaging permission"
        />
      </div>
    </Modal>
  )
}

export default function WhatsAppPage() {
  const [expandedFailures, setExpandedFailures] = useState<Record<string, boolean>>({})
  const [sendTestOrgId, setSendTestOrgId] = useState<string | null>(null)
  const [reconnectTarget, setReconnectTarget] = useState<{ orgId: string; webhookUrl?: string } | null>(null)
  const [editTarget, setEditTarget] = useState<EditSessionTarget | null>(null)
  const [metaCredTarget, setMetaCredTarget] = useState<string | null>(null)
  const [providerTab, setProviderTab] = useState<'meta-cloud' | 'baileys'>('meta-cloud')
  const [addDeviceOpen, setAddDeviceOpen] = useState(false)

  const {
    data: health,
    isError: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['wa-health'],
    queryFn: () => waProxy<HealthPayload>('/health'),
    refetchInterval: false,
  })

  const {
    data: sessionsData,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: ['wa-sessions'],
    queryFn: () => waProxy<SessionsListPayload>('/api/sessions'),
    refetchInterval: false,
  })

  const {
    data: subscriptions = [],
  } = useQuery({
    queryKey: ['wa-subscriptions'],
    queryFn: listWhatsAppSubscriptions,
    staleTime: 30_000,
  })

  const { data: hubDevices = [], refetch: refetchHubDevices } = useQuery({
    queryKey: ['wa-hub-devices'],
    queryFn: async () => {
      try {
        return await listWhatsappHubDevices()
      } catch {
        return []
      }
    },
    staleTime: 15_000,
  })

  const { data: allPartners = [] } = useQuery({
    queryKey: ['wa-partners'],
    queryFn: listPartners,
    staleTime: 60_000,
  })

  const { data: allPartnerSlots = [], refetch: refetchPartnerSlots } = useQuery({
    queryKey: ['wa-partner-slots'],
    queryFn: getAllPartnerSlots,
    staleTime: 30_000,
  })

  const { data: jumpstartOrgs = [] } = useQuery({
    queryKey: ['jumpstart-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name')
      if (error) throw error
      return (data ?? []) as { id: string; name: string }[]
    },
    staleTime: 60_000,
  })

  const sessions = sessionsData?.sessions ?? []
  const healthOk = health ? health.status === 'ok' : healthError ? false : null

  const { data: failureCounts = {}, refetch: refetchFailureCounts } = useQuery({
    queryKey: ['wa-webhook-failure-counts', sessions.map((s) => s.orgId).sort().join(',')],
    enabled: sessions.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        sessions.map(async (session) => {
          try {
            const result = await waProxy<{ failures?: unknown[]; count?: number }>(
              `/api/sessions/${encodeURIComponent(session.orgId)}/webhook-failures`
            )
            const count = typeof result.count === 'number'
              ? result.count
              : Array.isArray(result.failures)
                ? result.failures.length
                : 0
            return [session.orgId, count] as const
          } catch {
            return [session.orgId, 0] as const
          }
        })
      )
      return Object.fromEntries(entries) as Record<string, number>
    },
    refetchInterval: false,
    staleTime: 10_000,
  })

  useEffect(()=>{
    const ids=[
      sharedPoll(async()=>(await refetchHealth()).isSuccess,HEALTH_POLL_MS),
      sharedPoll(async()=>(await refetchSessions()).isSuccess,SESSIONS_POLL_MS),
    ]
    if(sessions.length)ids.push(sharedPoll(async()=>(await refetchFailureCounts()).isSuccess,30_000))
    return()=>ids.forEach(stopPoll)
  },[refetchHealth,refetchSessions,refetchFailureCounts,sessions.length])

  const subByOrg = useMemo(() => {
    const m = new Map<string, (typeof subscriptions)[number]>()
    for (const row of subscriptions) {
      m.set(row.org_id, row)
    }
    return m
  }, [subscriptions])

  const orgNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of jumpstartOrgs) m.set(o.id, o.name)
    return m
  }, [jumpstartOrgs])

  const mergedRows: MergedSessionRow[] = useMemo(() => {
    const seenDevice = new Set<string>()
    const seenOrg = new Set<string>()
    const rows: MergedSessionRow[] = []

    for (const s of sessions) {
      seenDevice.add(`${s.orgId}:${s.provider ?? 'baileys'}`)
      seenOrg.add(s.orgId)
      const sub = subByOrg.get(s.orgId)
      const orgName =
        sub?.organization_name ?? orgNameById.get(s.orgId) ?? '—'
      rows.push({
        orgId: s.orgId,
        organization: orgName,
        phoneDisplay: formatPhone(s.phoneNumber),
        statusKey: s.status,
        hasLiveSession: true,
        noSubscription: !sub,
        webhookUrl: s.webhookUrl,
        platform: s.platform,
        provider: s.provider,
      })
    }

    // Include Hub devices that may not appear in live /api/sessions
    // (notably official Meta Cloud registrations managed outside Baileys runtime).
    for (const d of hubDevices) {
      const deviceKey = `${d.org_id}:${d.provider ?? 'baileys'}`
      if (seenDevice.has(deviceKey)) continue
      seenDevice.add(deviceKey)
      seenOrg.add(d.org_id)
      const sub = subByOrg.get(d.org_id)
      const sessionProvider = sessions.find((s) => s.orgId === d.org_id)?.provider
      const inferredProvider = d.provider ?? sessionProvider ?? inferProviderFromLabel(d.label)
      rows.push({
        orgId: d.org_id,
        organization: sub?.organization_name ?? orgNameById.get(d.org_id) ?? '—',
        phoneDisplay: formatPhone(d.phone_number),
        statusKey: 'offline',
        hasLiveSession: false,
        noSubscription: !sub,
        platform: 'jumpstart',
        provider: inferredProvider ?? undefined,
      })
    }

    for (const sub of subscriptions) {
      if (seenOrg.has(sub.org_id)) continue
      seenOrg.add(sub.org_id)
      rows.push({
        orgId: sub.org_id,
        organization: sub.organization_name ?? orgNameById.get(sub.org_id) ?? '—',
        phoneDisplay: '—',
        statusKey: 'offline',
        hasLiveSession: false,
        noSubscription: false,
      })
    }

    return rows.sort((a, b) => a.orgId.localeCompare(b.orgId))
  }, [sessions, subscriptions, hubDevices, subByOrg, orgNameById])

  const filteredRows = useMemo(() => {
    return mergedRows.filter((r) => r.provider === providerTab)
  }, [mergedRows, providerTab])

  const unknownProviderRows = useMemo(
    () => mergedRows.filter((r) => !r.provider),
    [mergedRows]
  )

  const partnerGroups: PartnerGroup[] = useMemo(() => {
    const orgToPartner = new Map<string, WaPartner>()
    for (const slot of allPartnerSlots as PartnerOrgSlot[]) {
      if (!slot.partner_id) continue
      const partner = allPartners.find((p) => p.id === slot.partner_id)
      if (partner) orgToPartner.set(slot.org_id, partner)
    }

    const partnerMap = new Map<string, PartnerGroup>()
    const directGroup: PartnerGroup = { partner: null, rows: [] }

    for (const row of filteredRows) {
      const partner = orgToPartner.get(row.orgId)
      if (partner) {
        if (!partnerMap.has(partner.id)) {
          partnerMap.set(partner.id, { partner, rows: [] })
        }
        partnerMap.get(partner.id)!.rows.push(row)
      } else {
        directGroup.rows.push(row)
      }
    }

    const groups = [...partnerMap.values()].sort((a, b) =>
      (a.partner!.display_name).localeCompare(b.partner!.display_name)
    )
    if (directGroup.rows.length > 0) groups.push(directGroup)
    return groups
  }, [filteredRows, allPartnerSlots, allPartners])

  const disconnect = async (orgId: string) => {
    if (!window.confirm(`Disconnect WhatsApp session for ${orgId}?`)) return
    try {
      await waProxy(`/api/sessions/${encodeURIComponent(orgId)}`, 'DELETE')
      toast.success(`Disconnected ${orgId}`)
      await refetchSessions()
      await refetchHealth()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to disconnect session.'
      toast.error(message)
    }
  }

  /** Removes pairing from disk (creds + meta). Next connect requires a new QR scan. */
  const deleteInstance = async (orgId: string) => {
    if (
      !window.confirm(
        `Permanently delete WhatsApp instance for ${orgId}?\n\n` +
          'This removes saved pairing data on the server. Reconnecting will require scanning the QR code again.'
      )
    ) {
      return
    }
    try {
      // DELETE + query works on older VPS builds; POST /:orgId/purge requires a recent deploy.
      await waProxy<{ purged?: boolean }>(
        `/api/sessions/${encodeURIComponent(orgId)}?purge=true`,
        'DELETE'
      )
      toast.success(`Instance data removed for ${orgId}`)
      await refetchSessions()
      await refetchHealth()
      await refetchFailureCounts()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete instance.'
      toast.error(message)
    }
  }

  const reconnect = async (orgId: string, webhookUrl?: string) => {
    try {
      await waProxy(`/api/sessions/${encodeURIComponent(orgId)}/start`, 'POST', {
        webhookUrl: webhookUrl || undefined,
      })
      toast.success(`Start requested for ${orgId}`)
      await refetchSessions()
      await refetchFailureCounts()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to reconnect session.'
      toast.error(message)
      return
    }
  }

  const openQr = (orgId: string) => {
    window.open(`${CONNECT_BASE}${encodeURIComponent(orgId)}`, '_blank', 'noopener,noreferrer')
  }

  const saveEditedSession = async (
    oldOrgId: string,
    newOrgId: string,
    wh?: string,
    _platform?: string
  ) => {
    void _platform
    if (newOrgId === oldOrgId) {
      await reconnect(oldOrgId, wh)
    } else {
      await waProxy<{ success?: boolean }>(
        `/api/sessions/${encodeURIComponent(oldOrgId)}/migrate`,
        'POST',
        {
          targetOrgId: newOrgId,
          webhookUrl: wh,
        }
      )
      toast.success('Session moved — WhatsApp pairing kept (no new QR if creds are valid)')
    }
    await refetchSessions()
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">WhatsApp</h1>
        <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
          Service health, live sessions, and WhatsApp product subscriptions
        </p>
      </div>

      {/* Health banner */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 rounded-xl text-sm"
        style={{ background: '#252836', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
            style={{
              background: healthOk === false ? '#ef4444' : healthOk ? '#22c55e' : '#6b7280',
              boxShadow:
                healthOk === true ? '0 0 8px rgba(34,197,94,0.6)' : healthOk === false ? '0 0 6px rgba(239,68,68,0.5)' : 'none',
            }}
          />
          <span className="font-medium text-white whitespace-nowrap">WhatsApp Service</span>
        </div>
        {healthOk === false && (
          <span className="text-red-400">Service unreachable</span>
        )}
        {health && healthOk && (
          <>
            <span style={{ color: '#6b7280' }}>|</span>
            <span style={{ color: '#9ca3af' }}>
              <span className="text-white font-mono num">{health.sessions}</span> sessions
            </span>
            <span style={{ color: '#6b7280' }}>|</span>
            <span style={{ color: '#9ca3af' }}>
              <span className="text-white font-mono num">{health.connected}</span> connected
            </span>
            <span style={{ color: '#6b7280' }}>|</span>
            <span style={{ color: '#9ca3af' }}>
              Uptime <span className="text-white font-mono num">{formatUptime(health.uptime)}</span>
            </span>
            <span style={{ color: '#6b7280' }}>|</span>
            <span style={{ color: '#9ca3af' }}>
              RAM <span className="text-white font-mono num">{health.memoryMB}</span>MB
            </span>
          </>
        )}
      </div>

      {/* Sessions table */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex items-center gap-1 p-1 rounded-xl"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            {(['meta-cloud', 'baileys'] as const).map((tab) => {
              const label = tab === 'meta-cloud' ? '✅ Official' : '🔓 Unofficial'
              const count = mergedRows.filter((r) => r.provider === tab).length
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setProviderTab(tab)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    providerTab === tab
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {label}
                  <span className={`ml-1.5 text-xs ${providerTab === tab ? 'text-indigo-200' : 'text-gray-600'}`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              void refetchSessions()
              void refetchHealth()
              void refetchFailureCounts()
              void refetchHubDevices()
              void refetchPartnerSlots()
            }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-gray-300 hover:text-white transition-colors"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            <RefreshCw size={12} />
            Refresh
          </button>
        </div>
        {unknownProviderRows.length > 0 && (
          <div
            className="rounded-xl px-3 py-2 text-xs text-amber-200"
            style={{ background: '#7c2d1228', border: '1px solid #92400e66' }}
          >
            {unknownProviderRows.length} device(s) have unknown provider classification and are hidden from
            Official/Unofficial tabs until backfill completes.
          </div>
        )}
        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
          <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
            <thead>
              <tr style={{ background: '#252836', color: '#9ca3af' }}>
                <th className="text-left font-medium px-4 py-3">Platform · Org</th>
                <th className="text-left font-medium px-4 py-3">Organization</th>
                <th className="text-left font-medium px-4 py-3">Phone</th>
                <th className="text-left font-medium px-4 py-3">Provider</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Note</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center" style={{ color: '#6b7280' }}>
                    No sessions and no subscriptions for this product.
                  </td>
                </tr>
              )}
              {providerTab === 'meta-cloud' ? (
                filteredRows.map((row) => {
                  const failureCount = failureCounts[row.orgId] ?? 0
                  const expanded = !!expandedFailures[row.orgId]

                  return (
                    <Fragment key={`${row.orgId}:${row.provider ?? ''}`}>
                      <tr style={{ borderTop: '1px solid #2d3148' }}>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-400">{row.platform ?? 'jumpstart'}</span>
                          {' · '}
                          <span className="text-indigo-300">
                            {jumpstartOrgs.find((o) => o.id === row.orgId)?.name ?? row.orgId}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-200">
                          <div className="flex items-center gap-2">
                            <span>{row.organization}</span>
                            {failureCount > 0 && (
                              <button
                                type="button"
                                className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium text-red-200 bg-red-500/20 hover:bg-red-500/30"
                                onClick={() => {
                                  setExpandedFailures((prev) => ({
                                    ...prev,
                                    [row.orgId]: !prev[row.orgId],
                                  }))
                                }}
                              >
                                ⚠️ {failureCount}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-300 num">{row.phoneDisplay}</td>
                        <td className="px-4 py-3">
                          {row.provider === 'meta-cloud' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-300 border border-green-700/40">
                              ✅ Official
                            </span>
                          ) : row.provider === 'baileys' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/40">
                              🔓 Unofficial
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3"><StatusCell status={row.statusKey} /></td>
                        <td className="px-4 py-3">
                          {row.noSubscription && (
                            <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                              <AlertTriangle size={14} />
                              No subscription
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-end gap-2">
                            {/* Only for official devices from hub that may need credentials configured */}
                            {(() => {
                              const hubDev = hubDevices.find((d) => d.org_id === row.orgId && d.provider === 'meta-cloud')
                              const hasCreds = !!hubDev?.phone_number_id
                              return (
                                <button
                                  type="button"
                                  onClick={() => setMetaCredTarget(row.orgId)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
                                  style={{
                                    background: hasCreds ? '#16a34a20' : '#f59e0b20',
                                    color: hasCreds ? '#86efac' : '#fcd34d',
                                    border: `1px solid ${hasCreds ? '#16a34a40' : '#f59e0b40'}`,
                                  }}
                                  title={hasCreds ? 'Meta API credentials configured' : 'Meta API credentials missing'}
                                >
                                  {hasCreds ? '✅ API' : '⚠️ Configure API'}
                                </button>
                              )
                            })()}
                            {row.hasLiveSession && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditTarget({
                                    orgId: row.orgId,
                                    webhookUrl: row.webhookUrl,
                                    platform: row.platform,
                                  })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-200 hover:text-white"
                                style={{ background: '#3f3f4620' }}
                                title="Edit session"
                              >
                                <Pencil size={12} />
                                Edit
                              </button>
                            )}
                            {(row.statusKey === 'qr' || row.statusKey === 'connecting') && (
                              <button
                                type="button"
                                onClick={() => openQr(row.orgId)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-indigo-300 hover:text-white"
                                style={{ background: '#6366f120' }}
                              >
                                <ExternalLink size={12} />
                                Open QR
                              </button>
                            )}
                            {row.statusKey === 'connected' && (
                              <button
                                type="button"
                                onClick={() => setSendTestOrgId(row.orgId)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-cyan-200 hover:text-white"
                                style={{ background: '#06b6d420' }}
                              >
                                Send Test
                              </button>
                            )}
                            {row.hasLiveSession && (
                              <button
                                type="button"
                                onClick={() => disconnect(row.orgId)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-red-300 hover:text-red-100"
                                style={{ background: '#ef444420' }}
                              >
                                <Unplug size={12} />
                                Disconnect
                              </button>
                            )}
                            {row.hasLiveSession && (
                              <button
                                type="button"
                                onClick={() => deleteInstance(row.orgId)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-red-200 hover:text-red-50 border border-red-500/30"
                                style={{ background: '#7f1d1d25' }}
                                title="Remove pairing data from server; next connection needs a new QR"
                              >
                                <Trash2 size={12} />
                                Delete instance
                              </button>
                            )}
                            {(row.statusKey === 'disconnected' || row.statusKey === 'offline') && row.provider !== 'meta-cloud' && (
                              <button
                                type="button"
                                onClick={() => setReconnectTarget({ orgId: row.orgId, webhookUrl: row.webhookUrl })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-emerald-300 hover:text-emerald-100"
                                style={{ background: '#10b98120' }}
                              >
                                <RefreshCw size={12} />
                                Reconnect
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      <WebhookFailuresRow orgId={row.orgId} isExpanded={expanded} />
                    </Fragment>
                  )
                })
              ) : (
                partnerGroups.map((group) => (
                  <Fragment key={group.partner?.id ?? '__direct__'}>
                    <tr style={{ background: '#1e2133', borderTop: '1px solid #2d3148' }}>
                      <td colSpan={7} className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{group.partner ? '🤝' : '📋'}</span>
                          <span className="font-semibold text-white text-sm">
                            {group.partner ? group.partner.display_name : 'Direct'}
                          </span>
                          {group.partner && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              group.partner.status === 'active'
                                ? 'bg-green-900/40 text-green-300 border border-green-700/40'
                                : group.partner.status === 'trial'
                                  ? 'bg-blue-900/40 text-blue-300 border border-blue-700/40'
                                  : 'bg-red-900/40 text-red-300 border border-red-700/40'
                            }`}>
                              {group.partner.status}
                            </span>
                          )}
                          <span className="text-gray-500 text-xs ml-1">
                            {group.rows.length} device{group.rows.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.rows.map((row) => {
                      const failureCount = failureCounts[row.orgId] ?? 0
                      const expanded = !!expandedFailures[row.orgId]

                      return (
                        <Fragment key={`${row.orgId}:${row.provider ?? ''}`}>
                          <tr style={{ borderTop: '1px solid #2d3148' }}>
                            <td className="px-4 py-3">
                              <span className="text-xs text-gray-400">{row.platform ?? 'jumpstart'}</span>
                              {' · '}
                              <span className="text-indigo-300">
                                {jumpstartOrgs.find((o) => o.id === row.orgId)?.name ?? row.orgId}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-200">
                              <div className="flex items-center gap-2">
                                <span>{row.organization}</span>
                                {failureCount > 0 && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium text-red-200 bg-red-500/20 hover:bg-red-500/30"
                                    onClick={() => {
                                      setExpandedFailures((prev) => ({
                                        ...prev,
                                        [row.orgId]: !prev[row.orgId],
                                      }))
                                    }}
                                  >
                                    ⚠️ {failureCount}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-gray-300 num">{row.phoneDisplay}</td>
                            <td className="px-4 py-3">
                              {row.provider === 'meta-cloud' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-300 border border-green-700/40">
                                  ✅ Official
                                </span>
                              ) : row.provider === 'baileys' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/40">
                                  🔓 Unofficial
                                </span>
                              ) : (
                                <span className="text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3"><StatusCell status={row.statusKey} /></td>
                            <td className="px-4 py-3">
                              {row.noSubscription && (
                                <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                                  <AlertTriangle size={14} />
                                  No subscription
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditTarget({
                                      orgId: row.orgId,
                                      webhookUrl: row.webhookUrl,
                                      platform: row.platform,
                                    })}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-200 hover:text-white"
                                  style={{ background: '#3f3f4620' }}
                                  title="Edit session"
                                >
                                  <Pencil size={12} />
                                  Edit
                                </button>
                                {(row.statusKey === 'qr' || row.statusKey === 'connecting') && (
                                  <button
                                    type="button"
                                    onClick={() => openQr(row.orgId)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-indigo-300 hover:text-white"
                                    style={{ background: '#6366f120' }}
                                  >
                                    <ExternalLink size={12} />
                                    Open QR
                                  </button>
                                )}
                                {row.statusKey === 'connected' && (
                                  <button
                                    type="button"
                                    onClick={() => setSendTestOrgId(row.orgId)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-cyan-200 hover:text-white"
                                    style={{ background: '#06b6d420' }}
                                  >
                                    Send Test
                                  </button>
                                )}
                                {row.hasLiveSession && (
                                  <button
                                    type="button"
                                    onClick={() => disconnect(row.orgId)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-red-300 hover:text-red-100"
                                    style={{ background: '#ef444420' }}
                                  >
                                    <Unplug size={12} />
                                    Disconnect
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => deleteInstance(row.orgId)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-red-200 hover:text-red-50 border border-red-500/30"
                                  style={{ background: '#7f1d1d25' }}
                                  title="Remove pairing data from server; next connection needs a new QR"
                                >
                                  <Trash2 size={12} />
                                  Delete instance
                                </button>
                                {(row.statusKey === 'disconnected' || row.statusKey === 'offline') && (
                                  <button
                                    type="button"
                                    onClick={() => setReconnectTarget({ orgId: row.orgId, webhookUrl: row.webhookUrl })}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-emerald-300 hover:text-emerald-100"
                                    style={{ background: '#10b98120' }}
                                  >
                                    <RefreshCw size={12} />
                                    Reconnect
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          <WebhookFailuresRow orgId={row.orgId} isExpanded={expanded} />
                        </Fragment>
                      )
                    })}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-white">Jumpstart Hub devices</h2>
          <button
            type="button"
            onClick={() => setAddDeviceOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white"
            style={{ background: '#6366f1' }}
          >
            <Plus size={14} />
            Add device
          </button>
        </div>
        <p className="text-xs" style={{ color: '#9ca3af' }}>
          Rows created when a user adds a device in Jumpstart (Integrations). <code className="text-indigo-300">org_id</code>{' '}
          is the VPS session key (<code className="text-indigo-300">/connect/…</code>).
        </p>
        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
          <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
            <thead>
              <tr style={{ background: '#252836', color: '#9ca3af' }}>
                <th className="text-left font-medium px-4 py-3">Label</th>
                <th className="text-left font-medium px-4 py-3">Org ID (session)</th>
                <th className="text-left font-medium px-4 py-3">User email</th>
                <th className="text-left font-medium px-4 py-3">Phone (cached)</th>
                <th className="text-left font-medium px-4 py-3">Provider</th>
                <th className="text-left font-medium px-4 py-3">Billing</th>
                <th className="text-right font-medium px-4 py-3">Price / mo</th>
                <th className="text-left font-medium px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {hubDevices.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>
                    No Hub device rows yet — run DB migration <code className="text-gray-500">whatsapp_devices</code> or add a device in Jumpstart.
                  </td>
                </tr>
              )}
              {hubDevices.map((d) => (
                <tr key={d.id} style={{ borderTop: '1px solid #2d3148' }}>
                  <td className="px-4 py-3 text-gray-200">{d.label}</td>
                  <td className="px-4 py-3 font-mono text-xs text-indigo-300">{d.org_id}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{d.user_email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{d.phone_number ?? '—'}</td>
                  <td className="px-4 py-3">
                    {d.provider === 'meta-cloud' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-300 border border-green-700/40">
                        ✅ Official
                      </span>
                    ) : d.provider === 'baileys' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/40">
                        🔓 Unofficial
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-300 border border-red-700/40">
                        ⚠ Provider unknown
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{d.billing_status ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-right text-gray-300 num">
                    {(d.price_agorot ?? 0) > 0 ? formatNIS(d.price_agorot) : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <SubscriptionTable />

      <SendMessageModal
        orgId={sendTestOrgId ?? ''}
        isOpen={!!sendTestOrgId}
        onClose={() => setSendTestOrgId(null)}
      />

      <ReconnectModal
        orgId={reconnectTarget?.orgId ?? ''}
        currentWebhookUrl={reconnectTarget?.webhookUrl}
        isOpen={!!reconnectTarget}
        onClose={() => setReconnectTarget(null)}
        onConfirm={async (webhookUrl) => {
          if (!reconnectTarget?.orgId) return
          await reconnect(reconnectTarget.orgId, webhookUrl)
          setReconnectTarget(null)
        }}
      />

      <EditSessionModal
        session={editTarget}
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSave={saveEditedSession}
      />

      <MetaCredentialsModal
        orgId={metaCredTarget}
        isOpen={metaCredTarget !== null}
        onClose={() => setMetaCredTarget(null)}
        onSaved={() => void refetchHubDevices()}
      />

      <AddDeviceModal
        isOpen={addDeviceOpen}
        onClose={() => setAddDeviceOpen(false)}
        orgs={jumpstartOrgs}
        onCreated={() => void refetchHubDevices()}
      />
    </div>
  )
}
